import capitalize from "lodash/capitalize";
import clone from "lodash/clone";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import replace from "lodash/replace";
import startCase from "lodash/startCase";
import find from "lodash/find";
import filter from "lodash/filter";

import {
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  isBefore,
  isSameDay,
  startOfDay,
  format,
} from "date-fns";
import pluralize from "pluralize";

import { Capacitor } from "@capacitor/core";
import { Clipboard } from "@capacitor/clipboard";
import { isPlatform } from "@ionic/react";
import { useEffect } from "react";
import { Keyboard } from "@capacitor/keyboard";

import * as capacitorStorage from "./localStorage";
import { LOCAL_STORAGE } from "../config";
import {
  WLSubscriptionsTiers,
  PriceDiscoveryVersion,
} from "../models/subscriptions";
import { parsePhoneNumber } from "libphonenumber-js";
import { Giveaway } from "../models/giveaways";
import { EN_TEXT_ERRORS } from "../constants/error";
import { useWindowSize } from "react-use";

// eslint-disable-next-line react-hooks/exhaustive-deps
export const useEffectOnlyOnce = (func: () => void) => useEffect(func, []);

export const useBreakpoint = () => {
  const { width } = useWindowSize();

  if (width < 768) return "mobile";
  if (width < 1280) return "tablet";
  return "laptop";
};

export const isMobile = (allowMobileWeb = false) =>
  (isPlatform("ios") || isPlatform("android")) &&
  (!allowMobileWeb
    ? !isPlatform("mobileweb") &&
      (!isPlatform("desktop") ||
        (isPlatform("desktop") && isPlatform("hybrid")))
    : true);

export const isAndroid = () =>
  isPlatform("android") && !isPlatform("mobileweb");

export const isIOS = () => isPlatform("ios") && !isPlatform("mobileweb");

export const getDaysDifference = (date: Date) => {
  const currentDate = new Date();
  const diff = differenceInMinutes(currentDate, date);
  const dayMins = 24 * 60; //
  const counter = (diff - (diff % dayMins)) / dayMins;
  return counter;
};

export const keyboardListener = (callback: (didShow: boolean) => void) => {
  if (isMobile()) {
    Keyboard.addListener("keyboardWillShow", () => {
      callback(true);
    });

    Keyboard.addListener("keyboardDidHide", () => {
      callback(false);
    });

    return () => {
      Keyboard.removeAllListeners();
    };
  } else {
    callback(false);
    return () => {};
  }
};

export const blurActiveElement = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }
};

export const hideNativeKeyboard = (options?: { blurActiveElement?: boolean }) => {
  if (options?.blurActiveElement !== false) {
    blurActiveElement();
  }
  if (isMobile()) {
    void Keyboard.hide();
  }
};

export const numberWithCommas = (x: number) => {
  const result = (typeof x === "string" ? parseInt(x) : x || 0)
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const resultSplit = result.split(".");
  const remainder = result.split(".")[1];

  if (remainder === "00") {
    return resultSplit[0];
  } else {
    return result;
  }
};

export const matchesEntriesConfirmation = (
  input: string,
  totalEntries: number,
): boolean => input.trim().replace(/,/g, "") === `${totalEntries}`;

export const abbreviateNumber = (n: number) => {
  if (n < 1e3) return n + "";
  if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(3) + "K";
  if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(3) + "M";
  if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(3) + "B";
  if (n >= 1e12) return +(n / 1e12).toFixed(3) + "T";

  return "";
};

export const stripeAmountToDollars = (
  amountDecimal: string | number
): number => {
  const amount =
    typeof amountDecimal === "string"
      ? parseInt(amountDecimal, 10)
      : amountDecimal;
  return amount / 100;
};

export const isHistoricalDate = (date: Date) => {
  return isBefore(startOfDay(date), startOfDay(new Date()));
};

export const isSameDate = (date: Date, date2: Date) => {
  return isSameDay(date, date2);
};
export const encodeQueryData = (data: any) => {
  return Object.keys(data)
    .map(function (key: any) {
      return [key, data[key]].map(encodeURIComponent).join("=");
    })
    .join("&");
};

export const replaceAll = (
  data: string,
  replaceLetter: string,
  replaceWith: string
) => {
  return data
    .split("")
    .map((letter) => {
      if (letter === replaceLetter) {
        return replaceWith;
      } else {
        return letter;
      }
    })
    .join("");
};

export const blobToBase64 = (blob: Blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = (reader.result as string).split(",")[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  }) as Promise<string>;
};

export const executePromisesInSeries = async (
  promiseFns: (() => Promise<any>)[]
): Promise<any[]> => {
  const results: any[] = [];

  for (const promiseFn of promiseFns) {
    const result = await promiseFn();
    results.push(result);
  }

  return results;
};

export const checkIfCanVerifyMobile = async (
  callBack: (remainingTime: number) => void
) => {
  const recentMobileVerification = parseInt(
    (await capacitorStorage.getItem(LOCAL_STORAGE.recentMobileVerification)) ||
      "0"
  );

  const currentTime = new Date().valueOf();
  const timeLimit = 30000;
  const timePassed = currentTime - recentMobileVerification;

  let remainingTime = 0;
  if (timePassed > timeLimit) {
    capacitorStorage.setItem(
      LOCAL_STORAGE.recentMobileVerification,
      new Date().valueOf().toString()
    );
    remainingTime = timeLimit / 1000;
  } else if (timeLimit - timePassed > 0) {
    remainingTime = (timeLimit - timePassed) / 1000;
  }

  let coolDown = clone(remainingTime);
  let intervalId: NodeJS.Timeout | null = null;

  if (!!remainingTime) {
    // this.setState({ mobileVerificationCooldown: remainingTime });
    callBack(remainingTime);

    intervalId = setInterval(() => {
      const nextRemainingTime = coolDown - 1;
      if (nextRemainingTime <= 0) {
        coolDown = 0;
        callBack(0);
        clearInterval(intervalId!);
      } else {
        callBack(nextRemainingTime); // ~~ is roundoff
        coolDown = nextRemainingTime;
      }
    }, 1000);
  }

  return intervalId;
};

export const setRecentMobileVerification = () => {
  capacitorStorage.setItem(
    LOCAL_STORAGE.recentMobileVerification,
    new Date().valueOf().toString()
  );
};

export const calculateDays = (d: number) => {
  let months = 0,
    years = 0,
    days = 0,
    weeks = 0;
  if (d > 0) {
    while (d) {
      if (d >= 365) {
        years++;
        d -= 365;
      } else if (d >= 30) {
        months++;
        d -= 30;
      } else if (d >= 7) {
        weeks++;
        d -= 7;
      } else {
        days++;
        d--;
      }
    }
  }
  return {
    years,
    months,
    weeks,
    days,
  };
};

export const toIsoDateTime = (dateTimeString: Date): string => {
  const year = dateTimeString.getFullYear();
  const month = (dateTimeString.getMonth() + 1).toString().padStart(2, "0");
  const dateOfMonth = dateTimeString.getDate().toString().padStart(2, "0");
  const hour = dateTimeString.getHours().toString().padStart(2, "0");
  const minute = dateTimeString.getMinutes().toString().padStart(2, "0");

  return `${year}-${month}-${dateOfMonth}T${hour}:${minute}:00.000`;
};

export const isUserStillSubscribed = ({
  dateSubscribed,
  subscription,
}: {
  dateSubscribed: number;
  subscription?: WLSubscriptionsTiers;
}) => {
  return dateSubscribed && !isEmpty(subscription);
};

/**
 * Applies stripePriceVersionEntitlements to a subscription based on alternativePriceVersion.
 * Only applies for Stripe subscriptions (platform === "stripe") and non-one-off subscriptions.
 * Returns the subscription without stripePriceVersionEntitlements field.
 *
 * @param subscription - The subscription to apply entitlements to
 * @param alternativePriceVersion - The price version to use (version1 or version2)
 * @returns Subscription with entitlements applied and stripePriceVersionEntitlements removed
 */
export const applyStripePriceVersionEntitlements = (
  subscription: WLSubscriptionsTiers,
  alternativePriceVersion?: PriceDiscoveryVersion
): Omit<WLSubscriptionsTiers, "stripePriceVersionEntitlements"> => {
  // Only apply for Stripe subscriptions that are not one-off

  if (
    subscription.platform !== "stripe" ||
    subscription.isOneOff ||
    !alternativePriceVersion
  ) {
    const { stripePriceVersionEntitlements, ...rest } = subscription;
    return rest;
  }

  // Check if entitlements exist for this version
  const entitlements =
    subscription.stripePriceVersionEntitlements?.[alternativePriceVersion];

  if (!entitlements) {
    // No entitlements for this version, return without the field
    const { stripePriceVersionEntitlements, ...rest } = subscription;
    return rest;
  }

  // Apply entitlements: update entriesPerMonth, rewardPointsPerMonth, and benefits
  const { stripePriceVersionEntitlements, ...rest } = subscription;
  return {
    ...rest,
    entriesPerMonth: entitlements.entriesPerMonth,
    rewardPointsPerMonth: entitlements.rewardPointsPerMonth,
    benefits: entitlements.benefits,
  };
};

/**
 * Extracts the price from a subscription (Stripe or RevenueCat)
 * @param stripeSubscription - The Stripe subscription object (WLStripeWPlanSubscription) - optional
 * @param latestRevCatEvent - The RevenueCat event object - optional
 * @returns An object with price (number) and priceString (formatted string), or undefined if price not available
 */
export const getPriceFromSubscription = (
  stripeSubscription?:
    | {
        items?: {
          data?: Array<{
            price?: {
              unit_amount: number | null;
            };
          }>;
        };
      }
    | null
    | undefined,
  latestRevCatEvent?:
    | {
        event?: {
          price_in_purchased_currency?: number;
          currency?: string;
        };
      }
    | null
    | undefined
): { price: number; priceString: string } | undefined => {
  // Try Stripe subscription first
  if (
    stripeSubscription &&
    stripeSubscription.items?.data?.[0]?.price?.unit_amount !== null &&
    stripeSubscription.items?.data?.[0]?.price?.unit_amount !== undefined
  ) {
    const unitAmount = stripeSubscription.items.data[0].price.unit_amount;
    if (unitAmount !== null) {
      const price = unitAmount / 100;
      return {
        price,
        priceString: `$${price.toFixed()}`,
      };
    }
  }

  // Try RevenueCat event
  if (
    latestRevCatEvent?.event?.price_in_purchased_currency !== undefined &&
    latestRevCatEvent?.event?.price_in_purchased_currency !== null
  ) {
    const price = latestRevCatEvent.event.price_in_purchased_currency;
    const currency = latestRevCatEvent.event.currency || "USD";
    return {
      price,
      priceString: `${currency}${price.toFixed(2)}`,
    };
  }

  return undefined;
};

export const shuffle = (array: any[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    // Generate a random index
    const j = Math.floor(Math.random() * (i + 1));

    // Swap elements at i and j
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const normalizeAuthErrorCode = (code: string): string => {
  const trimmedCode = code.trim().toLowerCase();
  if (!trimmedCode) {
    return trimmedCode;
  }

  return trimmedCode.startsWith("auth/") ? trimmedCode : `auth/${trimmedCode}`;
};

const shouldTreatErrorCodeAsAuth = (code: string): boolean => {
  const trimmedCode = code.trim();
  if (!trimmedCode) {
    return false;
  }

  if (/^auth\//i.test(trimmedCode)) {
    return true;
  }

  if (/^err_/i.test(trimmedCode) || /^err[a-z0-9_]*$/i.test(trimmedCode)) {
    return false;
  }

  if (/errordomain/i.test(trimmedCode) || /[A-Z]/.test(trimmedCode)) {
    return false;
  }

  return true;
};

const isTechnicalUserFacingError = (message: string): boolean => {
  const normalized = message.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  if (/\bauth\/[a-z0-9_-]+\b/i.test(normalized)) {
    return true;
  }

  if (/\berr_[a-z0-9_-]+\b/i.test(normalized)) {
    return true;
  }

  if (/firebase:\s*error/i.test(normalized)) {
    return true;
  }

  if (/\bnsurlerrordomain\b/i.test(normalized)) {
    return true;
  }

  return false;
};

export const getErrorMsg = (
  error: any
): { errorMsg: string; rawCaseErrorMsg: string; rawErrorMsg: string } => {
  try {
    if (!error) {
      return {
        errorMsg: EN_TEXT_ERRORS.somethingWentWrong,
        rawCaseErrorMsg: EN_TEXT_ERRORS.somethingWentWrong,
        rawErrorMsg: EN_TEXT_ERRORS.somethingWentWrong,
      };
    }

    let result =
      (error.response?.data?.errors !== undefined &&
        error.response?.data?.errors[0]?.detail) ||
      error.response?.data?.result ||
      error.response?.data ||
      (typeof error.code === "string" &&
      shouldTreatErrorCodeAsAuth(error.code)
        ? normalizeAuthErrorCode(error.code)
        : undefined) ||
      error.message ||
      error;

    // Convert rawErrorMsg to string immediately, preserving object structure with JSON.stringify
    const rawErrorMsg =
      typeof result === "object" && result !== null
        ? JSON.stringify(result)
        : String(result);

    const extractObjectErrorMessage = (value: any): string | null => {
      if (!value || typeof value !== "object") {
        return null;
      }

      if (typeof value.error === "string" && value.error.trim()) {
        return value.error.trim();
      }
      if (typeof value.message === "string" && value.message.trim()) {
        return value.message.trim();
      }
      if (typeof value.rootCause === "string" && value.rootCause.trim()) {
        return value.rootCause.trim();
      }
      if (Array.isArray(value.errors) && value.errors.length > 0) {
        const firstError = value.errors[0];
        if (typeof firstError === "string" && firstError.trim()) {
          return firstError.trim();
        }
        if (
          firstError &&
          typeof firstError === "object" &&
          typeof firstError.message === "string" &&
          firstError.message.trim()
        ) {
          return firstError.message.trim();
        }
      }

      return null;
    };

    // Ensure result is a string before calling string methods
    if (typeof result !== "string") {
      const extracted = extractObjectErrorMessage(result);
      result = extracted || EN_TEXT_ERRORS.somethingWentWrong;
    }

    result = result.trim().toLowerCase();

    if (result.indexOf("auth/user-not-found") !== -1) {
      result = EN_TEXT_ERRORS.userNotFound;
    } else if (
      result.indexOf("auth/wrong-password") !== -1 ||
      result.indexOf("auth/invalid-credential") !== -1 ||
      result.indexOf("auth/invalid-login-credentials") !== -1
    ) {
      result = EN_TEXT_ERRORS.wrongPassword;
    } else if (result.indexOf("auth/network-request-failed") !== -1) {
      result = EN_TEXT_ERRORS.networkRequestFailed;
    } else if (
      result.indexOf("auth/nsurlerrordomain") !== -1 ||
      result.indexOf("nsurlerrordomain") !== -1
    ) {
      result = EN_TEXT_ERRORS.networkRequestFailed;
    } else if (result.indexOf("auth/email-already-in-use") !== -1) {
      result = EN_TEXT_ERRORS.emailAlreadyInUse;
    } else if (result.indexOf("auth/requires-recent-login") !== -1) {
      result = EN_TEXT_ERRORS.requiresRecentLogin;
    } else if (result.indexOf("auth/too-many-requests") !== -1) {
      result = EN_TEXT_ERRORS.tooManyRequests;
    } else if (result.indexOf("auth/invalid-email") !== -1) {
      result = EN_TEXT_ERRORS.invalidEmail;
    } else if (result.indexOf("Indexed Database") !== -1) {
      result = EN_TEXT_ERRORS.connectionLost;
    } else if (result.indexOf("500") !== -1) {
      result = EN_TEXT_ERRORS.somethingWentWrong;
    } else if (result.indexOf("Request failed with status code 400") !== -1) {
      result = EN_TEXT_ERRORS.requestFailed400;
    } else if (
      result.indexOf("not found") !== -1 &&
      result.indexOf("/") !== -1
    ) {
      result = EN_TEXT_ERRORS.somethingWentWrongConnection;
    } else if (result.indexOf("indexed database") !== -1) {
      result = EN_TEXT_ERRORS.oopsTroubleLoading;
    } else if (
      result.indexOf("VerificationCheck was not found".toLowerCase()) !== -1
    ) {
      result = EN_TEXT_ERRORS.otpTryAgain;
    } else if (result.indexOf("Invalid parameter `To`".toLowerCase()) !== -1) {
      result = EN_TEXT_ERRORS.twillioInvalidNumber;
    }

    if (isTechnicalUserFacingError(result)) {
      result = EN_TEXT_ERRORS.somethingWentWrong;
    }

    const finalErrorMsg = result
      .split(". ")
      .map((text: string) => capitalize(text))
      .join(". ");
    return {
      errorMsg: finalErrorMsg,
      rawCaseErrorMsg: result,
      rawErrorMsg,
    };
  } catch (processingError) {
    // If anything goes wrong processing the error, return a safe fallback
    const fallbackMsg = EN_TEXT_ERRORS.somethingWentWrong;
    return {
      errorMsg: fallbackMsg,
      rawCaseErrorMsg: fallbackMsg,
      rawErrorMsg: `Error processing failed: ${String(processingError)}`,
    };
  }
};

type SupportedCountries = "AU" | "PH" | "NZ";
export const validateNumber = (phoneNumber: string) => {
  const allowedCountries: { [countryCode: string]: SupportedCountries } = {
    "+61": "AU",
    "+63": "PH",
    "+64": "NZ",
  };

  const phoneCountryCode = phoneNumber.slice(0, 3);
  if (!(phoneCountryCode in allowedCountries)) {
    return "Phone numbers from your selected country are not yet supported.";
  }

  const trailingFormattedNumber = replaceAll(
    `${replace(phoneNumber.slice(3), /^0/, "")}`,
    " ",
    ""
  );
  try {
    const formattedNumber = parsePhoneNumber(
      `${phoneCountryCode}${trailingFormattedNumber}`,
      allowedCountries[phoneCountryCode]
    );

    if (!formattedNumber.isValid()) {
      return "The mobile number you entered is not valid. Please check the number and try again.";
    }
  } catch (error) {
    return "The mobile number you entered is not valid. Please check the number and try again.";
  }

  return true;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  try {
    const phoneCountryCode = phoneNumber.slice(0, 3);

    const trailingFormattedNumber = replaceAll(
      `${replace(phoneNumber.slice(3), /^0/, "")}`,
      " ",
      ""
    );

    return `${phoneCountryCode}${trailingFormattedNumber}`;
  } catch (error) {
    return phoneNumber;
  }
};

/**
 * Title-cases a name by splitting on whitespace only (hyphenated tokens stay one word, e.g. Jean-Paul).
 * Returns an empty string when `raw` is empty or whitespace-only.
 */
export const formatNameCapitalize = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed
    .split(/\s+/)
    .map((word) => capitalize(word.toLowerCase()))
    .join(" ");
};

export const UPPERCASE_FIELDS = ["firstName", "lastName"];
export const formatForm = (formData: any) => {
  const result = {} as { [key: string]: any };
  Object.keys(formData).forEach((key) => {
    const data = formData[key];
    result[key] = includes(UPPERCASE_FIELDS, key)
      ? startCase(data.toLowerCase())
      : data;
  });

  return result;
};

export const downloadJSONToCSV = (
  jsonList: Array<any>,
  filename: string = "export.csv"
): void => {
  // Generate the header row from the object keys
  const keys = Object.keys(jsonList[0]);
  let csvContent = keys.join(",") + "\n";

  // Generate the content rows
  jsonList.forEach((row) => {
    const values = keys.map((key) => {
      // Quote strings that contain commas or newlines
      const value = row[key];
      return typeof value === "string" && /[,\n]/.test(value)
        ? `"${value}"`
        : value;
    });
    csvContent += values.join(",") + "\n";
  });

  // Create a Blob with the CSV content
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  // Create a download link
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;

  // Append and trigger download, then remove link
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const copyToClipboard = async (text: string): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    await Clipboard.write({ string: text });
    return;
  }

  await navigator.clipboard.writeText(text);
};

export const getCountdownDisplay = (giveaway: Giveaway) => {
  const yetToOpen =
    giveaway.openDt !== undefined && giveaway.openDt > new Date().valueOf();
  const date = yetToOpen ? giveaway.openDt! : giveaway.giveAwayDt;

  const currentDate = new Date().getTime();
  const diff = date - currentDate;
  const day = 1000 * 60 * 60 * 24;
  const daysLeft = Math.round(diff / day);
  const countdown = calculateDays(daysLeft);

  const { years, months, weeks, days } = countdown;

  const yearLabel = years > 1 ? "YEARS" : "YEAR";
  const monthLabel = months > 1 ? "MONTHS" : "MONTH";
  const weekLabel = weeks > 1 ? "WEEKS" : "WEEK";
  const dayLabel = days > 1 ? "DAYS" : "DAY";

  const drawLabel = yetToOpen ? "DRAW OPENS IN" : "DRAW CLOSES IN";
  if (years) return `${drawLabel} ${years} ${yearLabel}`;
  else if (months) return `${drawLabel} ${months} ${monthLabel}`;
  else if (weeks) return `${drawLabel} ${weeks} ${weekLabel}`;
  else
    return days > 0 ? `${drawLabel} ${days} ${dayLabel}` : "DRAW OPENS TODAY";
};

export const getDrawnDateByDaysOrMonths = (drawnDate: number) => {
  const now = new Date();
  const targetDate = new Date(drawnDate);

  // If the drawn date is in the past, handle accordingly
  if (isBefore(targetDate, now)) return "0 Days";

  const diffMonths = differenceInMonths(targetDate, now);

  if (diffMonths < 1) {
    const diffDays = differenceInDays(targetDate, now);

    if (diffDays < 2) {
      return "1 Day";
    } else {
      return `${diffDays} Days`;
    }
  } else if (diffMonths === 1) {
    return "1 Month";
  } else {
    return `${diffMonths} Months`;
  }
};

export const formatDate = (
  dateStamp: number | undefined,
  formatStr: string
) => {
  if (!dateStamp) {
    return "N/A";
  }

  return format(new Date(dateStamp).getTime(), formatStr);
};

/**
 * Cart giveaway banner + order confirmation email — e.g. `Drawn 23rd April 2026`.
 */
export const formatGiveawayDrawDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  const month = date.toLocaleString("en-AU", { month: "long" });
  const year = date.getFullYear();
  return `Drawn ${day}${suffix} ${month} ${year}`;
};

/** Formatted dollar amount for UI (`$` + cents rounding). Negative: `-$12.50` (minus before `$`). */
export const formatDollarPriceDisplay = (amount: number): string => {
  const centsTotal = Math.round(amount * 100);
  const negative = centsTotal < 0;
  const absCents = Math.abs(centsTotal);

  const whole = Math.trunc(absCents / 100);
  const frac = absCents % 100;

  const sign = negative ? "-" : "";

  if (frac === 0) {
    return `${sign}$${whole}`;
  }

  return `${sign}$${whole}.${frac.toString().padStart(2, "0")}`;
};

export const hashStringToSHA256 = async (str: string) => {
  // Convert the string to an ArrayBuffer
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  // Hash the data with SHA-256
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // Convert the buffer to a hexadecimal string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return hashHex;
};

export interface TimeRemaining {
  days: number;
  hrs: number;
  mins: number;
  secs: number;
}

export const calculateTimeRemaining = (endDate: number): TimeRemaining => {
  const now = new Date().valueOf();
  const distance = endDate - now;
  if (distance < 0) {
    return convertTimeRemaining(0);
  }

  return convertTimeRemaining(distance);
};

export const convertTimeRemaining = (timeRemaining: number): TimeRemaining => {
  const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
  const hrs = Math.floor(
    (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const mins = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  return { days, hrs, mins, secs };
};

export const formatWithLeadingZero = (num: number) => {
  return num.toString().padStart(2, "0");
};

export const formatSecondsWithMinutes = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

// <T, K extends keyof T> This is to declare types that we can cast to the parameters, to make this function generic and typesafe
export const betterStringSearch = <T, K extends keyof T>(
  dataList: T[],
  searchQuery: string,
  fieldsToCheck: K[]
): T[] => {
  const queries = searchQuery.trim().toLowerCase().split(" ");

  return dataList.filter((item) => {
    const textsToCheck = fieldsToCheck
      .map((field) => item[field]?.toString().trim().toLowerCase())
      .join(" ")
      .split(" ");

    const matchedResult = find(
      textsToCheck,
      (text) => !isEmpty(filter(queries, (query) => text.indexOf(query) !== -1))
    );

    return !!matchedResult;
  });
};

export const isInAppBrowser = () => {
  try {
    const userAgent = window.navigator.userAgent || navigator.vendor;

    // Detect Android WebView (typically contains "wv" or "WebView")
    const isAndroidWebView = /Android/.test(userAgent) && /wv/.test(userAgent);

    // Detect iOS WebView (iOS devices running without Safari, or using WKWebView)
    const isIOSWebView =
      /iPhone|iPod|iPad/i.test(userAgent) && !/Safari/i.test(userAgent);

    // Return true if it's either an Android or iOS WebView
    return isAndroidWebView || isIOSWebView;
  } catch (error) {
    console.error("Error detecting in-app browser or WebView:", error);
    return true; // Default to true if error
  }
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

export const getInitials = (
  firstName: string | undefined,
  lastName: string | undefined
): string => {
  let initials: string = "";

  if (firstName && firstName.length > 0) {
    initials += firstName.charAt(0).toUpperCase();
  }

  if (lastName && lastName.length > 0) {
    initials += lastName.charAt(0).toUpperCase();
  }

  return initials;
};

export const isMacOs = () => {
  const userAgent = navigator.userAgent || (window as any).opera;
  if (/Mac/.test(userAgent)) {
    return true;
  }
  return false;
};

export const getModifiedFields = <T extends Record<string, any>>(
  original: T,
  updated: Partial<T>
): Partial<T> => {
  const changed: Partial<T> = {};

  Object.keys(updated).forEach((key) => {
    const k = key as keyof T;
    if (original[k] !== updated[k]) {
      changed[k] = updated[k];
    }
  });

  return changed;
};

export const COUNTRY_CODES = {
  AU: {
    value: "+61",
    woPlusSign: "61",
  },
  NZ: {
    value: "+64",
    woPlusSign: "64",
  },
  PH: {
    value: "+63",
    woPlusSign: "63",
  },
  US: {
    value: "+1",
    woPlusSign: "1",
  },
};

export const getPhoneNumberWithLeadingCountryCodeAndZero = (
  phoneNumber: string
) => {
  /**
   * Cases
   * 61419123123 -
   * +61419123123
   * 0419123123
   * 419123123
   */

  try {
    let unformattedNumber = replaceAll(phoneNumber.trim(), " ", "");
    let countryCode = "";

    for (const country of Object.values(COUNTRY_CODES)) {
      if (unformattedNumber.startsWith(country.value)) {
        countryCode = country.value;
        unformattedNumber = unformattedNumber.slice(country.value.length);
        break;
      }
      if (unformattedNumber.startsWith(country.woPlusSign)) {
        countryCode = country.value;
        unformattedNumber = unformattedNumber.slice(country.woPlusSign.length);
        break;
      }
    }

    if (unformattedNumber[0] === "0") {
      unformattedNumber = unformattedNumber.slice(1);
    }

    if (isEmpty(countryCode)) {
      countryCode = process.env.ENV === "production" ? "+61" : "+63";
    }

    return `${countryCode}0${unformattedNumber}`;
  } catch (error) {
    return phoneNumber;
  }
};

export const formatPhoneNumberToIntlAcceptedFormat = (phoneNumber: string) => {
  /**
   * Cases
   * 61419123123 -
   * +61419123123
   * 0419123123
   * 419123123
   */

  try {
    let unformattedNumber = replaceAll(phoneNumber.trim(), " ", "");
    let countryCode = "";

    for (const country of Object.values(COUNTRY_CODES)) {
      if (unformattedNumber.startsWith(country.value)) {
        countryCode = country.value;
        unformattedNumber = unformattedNumber.slice(country.value.length);
        break;
      }
      if (unformattedNumber.startsWith(country.woPlusSign)) {
        countryCode = country.value;
        unformattedNumber = unformattedNumber.slice(country.woPlusSign.length);
        break;
      }
    }

    if (unformattedNumber[0] === "0") {
      unformattedNumber = unformattedNumber.slice(1);
    }

    if (isEmpty(countryCode)) {
      countryCode = process.env.ENV === "production" ? "+61" : "+63";
    }

    return `${countryCode}${unformattedNumber}`;
  } catch (error) {
    return phoneNumber;
  }
};

export const formatWordPluralize = (word: string, count: number) => {
  return pluralize(pluralize.singular(word), count);
};

export const formatPluralRewards = <T>(
  rewards: T[],
  getValue: (reward: T) => number,
  singular: string
): string => {
  const values = rewards
    .map(getValue)
    .filter((v) => !isNaN(v))
    .sort((a, b) => a - b);

  if (values.length === 0) return "";
  if (values.length === 1) {
    const word = formatWordPluralize(singular, values[0]);
    return `${values[0]} ${word}`;
  } else if (values.length === 2) {
    const firstWord = formatWordPluralize(singular, values[0]);
    const secondWord = formatWordPluralize(singular, values[1]);
    return `${values[0]} ${firstWord} or ${values[1]} ${secondWord}`;
  } else {
    const firstWord = formatWordPluralize(singular, values[0]);
    const lastWord = formatWordPluralize(singular, values[values.length - 1]);
    return `${values[0]} ${firstWord} to ${values[values.length - 1]} ${lastWord}`;
  }
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Multi-currency money formatter for ecommerce surfaces (Shop, Cart, Checkout,
 * Product Details, etc.). Handles Shopify-style string amounts AND raw numbers,
 * defaults to AUD with 2-decimal precision (override `fractionDigits` to 0 for
 * whole-dollar displays like product price tiles). Empty/nullish amounts coerce
 * to 0; empty/nullish currency codes fall back to AUD so Intl.NumberFormat
 * never throws on missing data from upstream APIs.
 */
export const formatMoney = (
  amount: string | number,
  currencyCode = "AUD",
  fractionDigits = 2,
): string => {
  const numeric =
    typeof amount === "number" ? amount : Number(amount || "0");

  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currencyCode || "AUD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numeric);
};
